import React, { useState } from 'react';
import { validateProgram, dryRunProgram, commitProgram } from './addProgramApi';

// "Add Program" panel (Item C1). Paste a ProgramSpec YAML and either:
//   • Validate — check it against the same universal schema the loader enforces
//     (structure only, institution-agnostic); or
//   • Dry-run — additionally diff it against the SELECTED institution's catalog
//     (read-only): what a load would insert/delete, plus a course-existence
//     pre-flight. Neither writes; the actual load stays on the CLI
//     (load_yaml_program.py). The selected institution comes from the header
//     institution dropdown (X-Institution-Id, added by institutionFetch).

const SUMMARY_LABELS = [
  ['name', 'Name'],
  ['type', 'Type'],
  ['degree', 'Degree'],
  ['track', 'Track'],
  ['department', 'Department'],
  ['college', 'College'],
  ['institution', 'Institution'],
  ['credits_required', 'Credits required'],
  ['top_level_requirements', 'Top-level requirements'],
  ['total_requirements', 'Total requirements'],
  ['has_constraints', 'Has constraints'],
];

const ACTION_STYLES = {
  insert: 'bg-green-100 text-green-800',
  replace: 'bg-amber-100 text-amber-800',
  noop: 'bg-gray-100 text-gray-700',
  blocked: 'bg-red-100 text-red-800',
};

function AddProgram() {
  const [yamlText, setYamlText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [force, setForce] = useState(false);
  const [allowMissing, setAllowMissing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [confirmName, setConfirmName] = useState('');

  const run = async (fn) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setCommitResult(null);
    setConfirmName('');
    try {
      setResult(await fn());
    } catch (e) {
      setError(e.message || 'Request failed. Is the admin backend running?');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = () => run(() => validateProgram(yamlText));
  const handleDryRun = () => run(() => dryRunProgram(yamlText, { force, allowMissingCourses: allowMissing }));

  const handleCommit = async () => {
    setCommitting(true);
    setError(null);
    setCommitResult(null);
    try {
      setCommitResult(await commitProgram(yamlText, {
        force,
        allowMissingCourses: allowMissing,
        confirmProgramName: confirmName || null,
      }));
    } catch (e) {
      setError(e.message || 'Load request failed. Is the admin backend running?');
    } finally {
      setCommitting(false);
    }
  };

  const errorCount = result && !result.valid ? (result.error_count || (result.errors || []).length) : 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Add Program</h2>
        <p className="text-sm text-gray-500">
          Paste a ProgramSpec YAML. <span className="font-semibold">Validate</span> checks structure against
          the schema the loader uses; <span className="font-semibold">Dry-run</span> also diffs it against the
          selected institution's catalog (what a load would insert/delete + a course pre-flight). Both are
          read-only — the actual load stays on the CLI (<span className="font-mono">load_yaml_program.py</span>).
        </p>
      </div>

      <textarea
        aria-label="Program YAML"
        className="w-full h-80 font-mono text-xs border border-gray-300 rounded-md p-3"
        placeholder={'program:\n  name: ...\n  type: major\n  degree: BS\n  department: ...\n  institution: ...\n  credits_required: 120\nrequirements:\n  - name: ...\n    type: require_all\n    courses: [...]'}
        value={yamlText}
        onChange={(e) => setYamlText(e.target.value)}
      />

      <div className="flex flex-wrap items-center gap-4">
        <button
          onClick={handleValidate}
          disabled={loading || !yamlText.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 hover:bg-blue-700 transition"
        >
          {loading ? 'Working…' : 'Validate'}
        </button>
        <button
          onClick={handleDryRun}
          disabled={loading || !yamlText.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:opacity-50 hover:bg-indigo-700 transition"
        >
          {loading ? 'Working…' : 'Dry-run (selected institution)'}
        </button>
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
          force (replace if already loaded)
        </label>
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <input type="checkbox" checked={allowMissing} onChange={(e) => setAllowMissing(e.target.checked)} />
          allow missing courses
        </label>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Validate result: schema summary */}
      {result && result.valid && result.summary && (
        <div className="rounded-md border border-green-300 bg-green-50 p-4 text-sm">
          <div className="font-semibold text-green-800 mb-2">✓ Valid ProgramSpec — summary</div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
            {SUMMARY_LABELS.map(([key, label]) => {
              const raw = result.summary[key];
              const value = typeof raw === 'boolean' ? (raw ? 'yes' : 'no') : (raw == null || raw === '' ? '—' : raw);
              return (
                <div key={key} className="flex justify-between border-b border-green-100 py-0.5">
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="text-gray-900 font-medium">{String(value)}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      {/* Dry-run result: plan + course pre-flight */}
      {result && result.valid && result.plan && (
        <div className="rounded-md border border-indigo-300 bg-indigo-50 p-4 text-sm space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-indigo-900">Dry-run plan</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${ACTION_STYLES[result.plan.action] || 'bg-gray-100 text-gray-700'}`}>
              {result.plan.action}
            </span>
            <span className="text-xs text-gray-500">
              {result.program.name} / {result.program.track} · {result.program.institution}
            </span>
          </div>

          <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
            <div className="flex justify-between"><dt className="text-gray-500">Program version</dt><dd className="font-medium">{result.plan.program_version}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Already loaded</dt><dd className="font-medium">{result.plan.already_loaded ? 'yes' : 'no'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Requirements (new)</dt><dd className="font-medium">{result.plan.requirements_new}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Files +/−</dt><dd className="font-medium">+{result.plan.adrl_files_to_insert} / −{result.plan.adrl_files_to_delete}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Requirements +/−</dt><dd className="font-medium">+{result.plan.requirements_to_insert} / −{result.plan.requirements_to_delete}</dd></div>
          </dl>

          <div className={`text-xs ${result.course_preflight.ok ? 'text-green-700' : 'text-red-700'}`}>
            {result.course_preflight.ok
              ? '✓ Course pre-flight: all referenced courses exist'
              : `✗ Course pre-flight: ${result.course_preflight.missing.length} missing course(s) — ${result.course_preflight.missing.join(', ')}`}
          </div>
          {result.course_preflight.substitutions && Object.keys(result.course_preflight.substitutions).length > 0 && (
            <div className="text-xs text-amber-700">
              Historical aliases (update the YAML to the canonical codes):{' '}
              {Object.entries(result.course_preflight.substitutions).map(([a, c]) => `${a}→${c}`).join(', ')}
            </div>
          )}
          {result.course_preflight.unseeded_aliases && Object.keys(result.course_preflight.unseeded_aliases).length > 0 && (
            <div className="text-xs text-red-700" data-testid="unseeded-aliases">
              Load aliases first — {Object.keys(result.course_preflight.unseeded_aliases).length} of the missing code(s) are aliases in a committed CSV not yet seeded into course_aliases:{' '}
              {Object.entries(result.course_preflight.unseeded_aliases).map(([a, csv]) => `${a} (${csv})`).join(', ')}
            </div>
          )}
          {result.plan.action === 'noop' && (
            <div className="text-xs text-gray-500">Already loaded — check <span className="font-mono">force</span> to plan a replace.</div>
          )}
          {result.plan.action === 'blocked' && (
            <div className="text-xs text-red-700">Blocked by missing courses — load them first, add aliases, or check <span className="font-mono">allow missing courses</span>.</div>
          )}

          {(result.plan.action === 'insert' || result.plan.action === 'replace') && (
            <div className="pt-2 border-t border-indigo-200 space-y-2">
              {result.plan.action === 'replace' && (
                <div className="text-xs">
                  <label className="block text-red-700 mb-1">
                    Replacing this program deletes its {result.plan.requirements_to_delete} existing requirement(s).
                    Type the exact program name to confirm: <span className="font-mono">{result.program.name}</span>
                  </label>
                  <input
                    aria-label="Confirm program name"
                    className="w-full border border-gray-300 rounded px-2 py-1 font-mono"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={result.program.name}
                  />
                </div>
              )}
              <button
                onClick={handleCommit}
                disabled={committing || (result.plan.action === 'replace' && confirmName !== result.program.name)}
                className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50 hover:bg-red-700 transition"
              >
                {committing ? 'Loading…' : result.plan.action === 'replace' ? 'Replace in catalog' : 'Load to catalog'}
              </button>
              <span className="ml-2 text-xs text-gray-500">Writes to the selected institution's catalog.</span>
            </div>
          )}
        </div>
      )}

      {commitResult && (
        <div className={`rounded-md border p-4 text-sm ${commitResult.loaded ? 'border-green-300 bg-green-50 text-green-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
          {commitResult.loaded ? (
            <span>
              <span className="font-semibold">✓ Loaded to catalog.</span>{' '}
              {commitResult.plan.requirements_to_insert} requirement(s) written
              {commitResult.plan.action === 'replace' ? `, ${commitResult.plan.requirements_to_delete} replaced` : ''}.
              {commitResult.cache_invalidated === false && (
                <span className="text-red-700"> ⚠ cache invalidation failed — the app may show stale requirements until it refreshes.</span>
              )}
            </span>
          ) : (
            <span className="font-semibold">
              Not loaded{commitResult.reason ? ` — ${String(commitResult.reason).replace(/_/g, ' ')}` : ''}.
              {(commitResult.errors || []).length > 0 ? ' ' + commitResult.errors.join('; ') : ''}
            </span>
          )}
        </div>
      )}

      {/* Errors (validate or dry-run) */}
      {result && !result.valid && (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm">
          <div className="font-semibold text-red-800 mb-2">
            {result.stage === 'yaml'
              ? 'YAML parse error'
              : result.stage === 'schema'
              ? `Schema validation failed (${errorCount} error${errorCount === 1 ? '' : 's'})`
              : 'Validation failed'}
          </div>
          <ul className="space-y-1">
            {(result.errors || []).map((err, i) => (
              <li key={i} className="font-mono text-xs text-red-700">
                {typeof err === 'string' ? err : `${err.loc ? err.loc + ': ' : ''}${err.msg}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AddProgram;
